import { Component } from '@angular/core';
import { InAppPurchase } from '@ionic-native/in-app-purchase';
import { AlertController, LoadingController, NavController, NavParams, Platform } from 'ionic-angular';
import { Api } from '../../providers/api';
import { SubscriptionService } from '../../providers/subscription-service';
import { SKErrorCode } from './sk-error-code';
import { createOfflineCompileUrlResolver } from '@angular/compiler';
import { TermsAndConditionsPage } from '../terms-and-conditions/terms-and-conditions';
import { PrivacyPolicyPage } from '../privacy-policy/privacy-policy';
import { FirebaseAnalytics } from '@ionic-native/firebase-analytics';

interface Product {
    currency: string;
    description: string;
    price: string;
    priceAsDecimal: number;
    productId: string;
    title: string;
}

interface Receipt {
    autoRenewing: boolean;
    orderId: string;
    packageName: string;
    productId: string;
    purchaseState: number;
    purchaseTime: number;
    purchaseToken: string;
}

interface Purchase {
    productId: string;
    productType: string;
    receipt: string;
    signature: string;
    transactionid: string;
    type: string;
}

interface IosPurchase {
    tranactionId: string;
    receipt: string;
}

@Component({
  selector: 'page-subscriptions',
  templateUrl: 'subscriptions.html'
})

export class SubscriptionsPage {

    public allowCode: boolean = false;
    public product: Product = null;
    public type: 'standard' | 'promo' = 'standard';
    public promoCode: string = '';
    public storeName: string = 'store';

    constructor(
        public alertCtrl: AlertController,
        public analytics: FirebaseAnalytics,
        public api: Api,
        public inAppPurchase: InAppPurchase,
        public loadingCtrl: LoadingController,
        public navCtrl: NavController,
        public navParams: NavParams,
        public platform: Platform,
        public subscriptionService: SubscriptionService,
    ) {
        this.allowCode = this.navParams.get('allowCode');
        this.product = this.navParams.get('product');
        this.type = this.navParams.get('type');

        if (this.platform.is('android')) this.storeName = 'Google Play';
        if (this.platform.is('ios')) this.storeName = 'iTunes';
    }

    public ionViewDidEnter() {
        this.analytics.setCurrentScreen('Subscriptions');
    }

    public async reloadProduct() {
        let productIds = await this.api.getProductIds().toPromise();
        let productId;

        if (this.platform.is('android')) productId = productIds.product_ids.android;
        if (this.platform.is('ios')) productId = productIds.product_ids.ios;

        let products = await this.inAppPurchase.getProducts([productId]);

        this.product = products[0];
        this.type = productIds.type;
    }

    public async getExistingPurchaseOfProductId(productId: string): Promise<Purchase> {
        let purchases: Purchase[] = await this.inAppPurchase.restorePurchases();
        for (let purchase of purchases) {
            if (purchase.productId == productId) return purchase;
        }
        return null;
    }

    public async getExistingIosReceipt(): Promise<string> {
        try {
            let purchases: any[] = await this.inAppPurchase.restorePurchases();
            if (typeof purchases !== 'object' || purchases.length < 1) return null;
            return await this.inAppPurchase.getReceipt();
        } catch (error) {
            return null;
        }
    }

    public async applyPromoCode() {
        let loading = this.loadingCtrl.create();
        loading.present();

        try {
            let result = await this.api.postApplyDiscountCode({
                code: this.promoCode
            }).toPromise();

            if (result.success) await this.reloadProduct();
        } catch (error) {
            this.showError(error.message);
        } finally {
            loading.dismiss();
        }
    }

    public addSubscription() {
        if (this.platform.is('android')) return this.addAndroidSubscription();
        if (this.platform.is('ios')) return this.addIosSubscription();
    }

    public async addAndroidSubscription() {
        let loading = this.loadingCtrl.create();
        loading.present();

        try {
            let newPurchase: {
                transactionId: string;
                receipt: string;
                signature: string;
                productType: string;
            };
            let existingPurchase: Purchase;

            existingPurchase = await this.getExistingPurchaseOfProductId(this.product.productId);
            if (!existingPurchase) newPurchase = await this.inAppPurchase.subscribe(this.product.productId);

            let receipt: Receipt = JSON.parse(existingPurchase ? existingPurchase.receipt : newPurchase.receipt);

            await this.api.postActivateAndroidSubscription({
                purchase_token: receipt.purchaseToken,
                subscription_id: receipt.productId,
            }).retryWhen(errors => errors.delay(1000).take(10)).toPromise();

            let subscription = await this.subscriptionService.fetch();
            this.subscriptionService.setAndStore(subscription);

            let alert = this.alertCtrl.create({
                title: 'Success',
                message: 'Your subscription has been confirmed!',
                buttons: [{
                    text: 'OK',
                    handler: () => {
                        this.navCtrl.pop();
                    }
                }]
            });

            alert.present();
        } catch (error) {
            if (error.errorCode && error.errorCode == -5) return;

            let alert = this.alertCtrl.create({
                title: 'Error',
                message: 'An error occured while confirming your subscription. Please try again.',
                buttons: [{
                    text: 'OK'
                }]
            });

            alert.present();
        } finally {
            loading.dismiss();
        }
    }

    public async addIosSubscription() {
        let loading = this.loadingCtrl.create();
        loading.present();

        try {
            let newPurchase: IosPurchase;
            let existingReceipt = await this.getExistingIosReceipt();
            if (!existingReceipt) newPurchase = await this.inAppPurchase.subscribe(this.product.productId) as any as IosPurchase;

            let receipt: string = existingReceipt || newPurchase.receipt;

            console.log('Receipt', receipt);

            await this.api.postActivateIosSubscription({
                receipt: receipt
            }).retryWhen(errors => errors.delay(1000).take(20)).toPromise();

            let subscription = await this.subscriptionService.fetch();
            this.subscriptionService.setAndStore(subscription);

            let alert = this.alertCtrl.create({
                title: 'Success',
                message: 'Your subscription has been confirmed!',
                buttons: [{
                    text: 'OK',
                    handler: () => {
                        this.navCtrl.pop();
                    }
                }]
            });

            alert.present();
        } catch (error) {
            if (error.errorCode && error.errorCode == SKErrorCode.paymentCancelled) return;

            let alert = this.alertCtrl.create({
                title: 'Error',
                message: 'An error occured while confirming your subscription. Please try again.',
                buttons: [{
                    text: 'OK'
                }]
            });

            alert.present();
        } finally {
            loading.dismiss();
        }
    }

    public showError(message: string) {
        let alert = this.alertCtrl.create({
            title: 'Error',
            message: message,
            buttons: [{
                text: 'OK'
            }]
        });

        alert.present();
    }

    public showTermsOfUse() {
        this.navCtrl.push(TermsAndConditionsPage);
    }

    public showPrivacyPolicy() {
        this.navCtrl.push(PrivacyPolicyPage);
    }

}



// WEBPACK FOOTER //
// ./src/pages/subscriptions/subscriptions.ts